import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';

import { ProjectTemplateEnum } from '@jira/polaris__domain-project/src/ProjectMetadata.types.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { getOnboardingInformation } from '@jira/polaris__remote-project/src/getOnboardingInformation.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	type Action,
	createStore,
	createActionsHook,
	createStateHook,
	type BoundActions,
	type HookActionsFunction,
	type HookStateFunction,
} from '@atlassian/react-sweet-state';
import {
	setUserProperties,
	getUserProperty,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import { createErrorAnalytics } from '@jira/polaris__lib-errors/src/createErrorAnalytics.tsx';
import { UnknownError } from '@jira/polaris__lib-errors/src/UnknownError.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import type { ProjectContext } from '@jira/platform__services-providers-project-context/src/types.tsx';
import { BANNER_HEIGHT, BANNER_NAME } from './bannerName.constants.tsx';
import type { State } from './State.types.tsx';

type AccountId = string;

function getError(error: unknown): Error {
	if (error instanceof Error) {
		return error;
	}

	return new UnknownError('Retrieved non-standard error', error);
}

export const initialState: State = {
	isDismissed: false,
	height: BANNER_HEIGHT,
	shouldShow: false,
	isLoading: true,
};

export const actions = {
	onDismiss:
		(accountId: AccountId): Action<State> =>
		async ({ setState }) => {
			if (!accountId) {
				return;
			}

			// Update UI immediately - optimistic dismissal
			setState({
				isDismissed: true,
			});

			try {
				await setUserProperties(accountId, BANNER_NAME, 'false');
			} catch (error) {
				fireErrorAnalytics(
					createErrorAnalytics('polaris.jpd-blueprints-feedback.banner-dismissed', getError(error)),
				);
			}
		},

	fetchBannerVisibility:
		(accountId: AccountId, projectId?: ProjectContext['projectId']): Action<State> =>
		async ({ setState, getState }) => {
			if (!accountId) {
				return;
			}

			// Don't show the banner if projectId is not provided
			if (!projectId) {
				setState({
					shouldShow: false,
				});

				return;
			}

			try {
				const onboardingInformation = await getOnboardingInformation(String(projectId));

				// Show the banner only for BLUEPRINTS project
				if (onboardingInformation.initialProjectTemplate !== ProjectTemplateEnum.BLUEPRINTS) {
					setState({
						shouldShow: false,
						isLoading: false,
					});

					return;
				}

				const shouldShow = await getUserProperty<boolean>(accountId, BANNER_NAME);

				setState({
					shouldShow,
					isLoading: false,
				});
			} catch (error) {
				const { isDismissed } = getState();

				// Show the banner only if it has not been dismissed
				setState({
					shouldShow: !isDismissed,
					isLoading: false,
				});

				if (
					(error instanceof FetchError && error.statusCode === 404) ||
					isClientFetchError(error)
				) {
					return;
				}

				fireErrorAnalytics(
					createErrorAnalytics('polaris.jpd-blueprints-feedback.banner-dismissed', getError(error)),
				);
			}
		},
} as const;

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	name: 'PolarisBlueprintsFeedbackBanner',
	initialState,
	actions,
});

export const useJpdBlueprintsFeedbackBannerState: HookStateFunction<State, void> =
	createStateHook(Store);
export const useJpdBlueprintsFeedbackBannerActions: HookActionsFunction<
	BoundActions<
		State,
		{
			readonly onDismiss: (accountId: AccountId) => Action<State>;
			readonly fetchBannerVisibility: (
				accountId: AccountId,
				projectId?: ProjectContext['projectId'],
			) => Action<State>;
		}
	>
> = createActionsHook(Store);
